import React, { FC } from 'react';
import { Ellipse, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import {
  Annotation,
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  DocumentRotation,
  PenAnnotation,
  RectangleAnnotation,
  TextAnnotation,
} from '../../types';
import {
  ARROW_HEAD_BASE_LEN,
  ARROW_HEAD_HALF_ANGLE,
  ARROW_HEAD_STROKE_FACTOR,
  ARROW_LINE_RECESS_FACTOR,
  HIGHLIGHTER_OPACITY,
  HIGHLIGHTER_WIDTH_MULTIPLIER,
  HIGHLIGHT_ERASER_COLOR,
  HIGHLIGHT_ERASER_OPACITY,
  HIGHLIGHT_HOVER_COLOR,
  HIGHLIGHT_HOVER_OPACITY,
  HIGHLIGHT_SELECTED_COLOR,
  HIGHLIGHT_SELECTED_OPACITY,
  HIGHLIGHT_STROKE_OUTSET,
  RECTANGLE_CORNER_RADIUS,
  SHAPE_FILL_OPACITY,
  TEXT_HIGHLIGHT_PADDING,
  TEXT_MIN_WIDTH,
  TEXT_WIDTH_PER_CHAR_FACTOR,
} from '../../constants';
import { getSvgPathFromPoints } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';

export interface AnnotationShapeProps {
  annotation: Annotation;
  isSelected: boolean;
  isEraserHover: boolean;
  isHovered: boolean;
  /** When the transparent input overlay is active over a text annotation, hide the SVG text. */
  isEditing: boolean;
  /** Live text value while editing — used to size the highlight rect for the active text annotation. */
  editingText?: string | null;
  /**
   * Document rotation in degrees. Text glyphs are flat single-anchor primitives, so they need an
   * explicit rotate transform (other shapes rotate naturally via their multi-point geometry).
   */
  documentRotation?: DocumentRotation;
}

interface HighlightInfo {
  color: string;
  opacity: number;
}

const resolveHighlight = ({
  isSelected,
  isEraserHover,
  isHovered,
}: Pick<AnnotationShapeProps, 'isSelected' | 'isEraserHover' | 'isHovered'>): HighlightInfo | null => {
  if (isSelected) return { color: HIGHLIGHT_SELECTED_COLOR, opacity: HIGHLIGHT_SELECTED_OPACITY };
  if (isEraserHover) return { color: HIGHLIGHT_ERASER_COLOR, opacity: HIGHLIGHT_ERASER_OPACITY };
  if (isHovered) return { color: HIGHLIGHT_HOVER_COLOR, opacity: HIGHLIGHT_HOVER_OPACITY };
  return null;
};

const renderPenOrHighlighter = (a: PenAnnotation, highlight: HighlightInfo | null) => {
  if (a.points.length < 2) return null;
  const d = getSvgPathFromPoints(a.points);
  const isHL = a.type === AnnotationType.Highlighter;
  const strokeWidth = isHL ? a.style.strokeWidth * HIGHLIGHTER_WIDTH_MULTIPLIER : a.style.strokeWidth;
  return (
    <G>
      {highlight ? (
        <Path
          d={d}
          fill="none"
          stroke={highlight.color}
          strokeWidth={strokeWidth + HIGHLIGHT_STROKE_OUTSET}
          opacity={highlight.opacity}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
      <Path
        d={d}
        fill="none"
        stroke={a.style.strokeColor}
        strokeWidth={strokeWidth}
        opacity={isHL ? HIGHLIGHTER_OPACITY : a.style.opacity}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </G>
  );
};

const renderArrow = (a: ArrowAnnotation, highlight: HighlightInfo | null) => {
  const angle = Math.atan2(a.end.y - a.start.y, a.end.x - a.start.x);
  const headLen = ARROW_HEAD_BASE_LEN + a.style.strokeWidth * ARROW_HEAD_STROKE_FACTOR;
  const tip1 = {
    x: a.end.x - headLen * Math.cos(angle - ARROW_HEAD_HALF_ANGLE),
    y: a.end.y - headLen * Math.sin(angle - ARROW_HEAD_HALF_ANGLE),
  };
  const tip2 = {
    x: a.end.x - headLen * Math.cos(angle + ARROW_HEAD_HALF_ANGLE),
    y: a.end.y - headLen * Math.sin(angle + ARROW_HEAD_HALF_ANGLE),
  };
  const lineEndX = a.end.x - headLen * ARROW_LINE_RECESS_FACTOR * Math.cos(angle);
  const lineEndY = a.end.y - headLen * ARROW_LINE_RECESS_FACTOR * Math.sin(angle);
  return (
    <G>
      {highlight ? (
        <Line
          x1={a.start.x}
          y1={a.start.y}
          x2={a.end.x}
          y2={a.end.y}
          stroke={highlight.color}
          strokeWidth={a.style.strokeWidth + HIGHLIGHT_STROKE_OUTSET}
          opacity={highlight.opacity}
        />
      ) : null}
      <Line
        x1={a.start.x}
        y1={a.start.y}
        x2={lineEndX}
        y2={lineEndY}
        stroke={a.style.strokeColor}
        strokeWidth={a.style.strokeWidth}
        opacity={a.style.opacity}
        strokeLinecap="round"
      />
      <Polygon points={`${a.end.x},${a.end.y} ${tip1.x},${tip1.y} ${tip2.x},${tip2.y}`} fill={a.style.strokeColor} opacity={a.style.opacity} />
    </G>
  );
};

const renderRectangle = (a: RectangleAnnotation, highlight: HighlightInfo | null) => (
  <G>
    {highlight ? (
      <Rect
        x={a.origin.x}
        y={a.origin.y}
        width={a.width}
        height={a.height}
        fill="none"
        stroke={highlight.color}
        strokeWidth={a.style.strokeWidth + HIGHLIGHT_STROKE_OUTSET}
        opacity={highlight.opacity}
        rx={RECTANGLE_CORNER_RADIUS}
      />
    ) : null}
    {a.filled ? (
      <Rect
        x={a.origin.x}
        y={a.origin.y}
        width={a.width}
        height={a.height}
        fill={a.style.strokeColor}
        fillOpacity={SHAPE_FILL_OPACITY}
        stroke="none"
        rx={RECTANGLE_CORNER_RADIUS}
      />
    ) : null}
    <Rect
      x={a.origin.x}
      y={a.origin.y}
      width={a.width}
      height={a.height}
      fill="none"
      stroke={a.style.strokeColor}
      strokeWidth={a.style.strokeWidth}
      opacity={a.style.opacity}
      rx={RECTANGLE_CORNER_RADIUS}
    />
  </G>
);

const renderCircle = (a: CircleAnnotation, highlight: HighlightInfo | null) => (
  <G>
    {highlight ? (
      <Ellipse
        cx={a.center.x}
        cy={a.center.y}
        rx={a.radiusX}
        ry={a.radiusY}
        fill="none"
        stroke={highlight.color}
        strokeWidth={a.style.strokeWidth + HIGHLIGHT_STROKE_OUTSET}
        opacity={highlight.opacity}
      />
    ) : null}
    {a.filled ? (
      <Ellipse
        cx={a.center.x}
        cy={a.center.y}
        rx={a.radiusX}
        ry={a.radiusY}
        fill={a.style.strokeColor}
        fillOpacity={SHAPE_FILL_OPACITY}
        stroke="none"
      />
    ) : null}
    <Ellipse
      cx={a.center.x}
      cy={a.center.y}
      rx={a.radiusX}
      ry={a.radiusY}
      fill="none"
      stroke={a.style.strokeColor}
      strokeWidth={a.style.strokeWidth}
      opacity={a.style.opacity}
    />
  </G>
);

const renderText = (
  a: TextAnnotation,
  highlight: HighlightInfo | null,
  isEditing: boolean,
  editingText: string | null | undefined,
  rotation: DocumentRotation
) => {
  // While the inline input is mounted, the saved annotation text isn't updated until commit;
  // size the highlight rect from the live `editingText` so the blue area grows with typing.
  const liveText = isEditing && editingText != null ? editingText : a.text;
  const textWidthApprox = Math.max(liveText.length * a.fontSize * TEXT_WIDTH_PER_CHAR_FACTOR, TEXT_MIN_WIDTH);
  const highlightRect = highlight ? (
    <Rect
      x={a.position.x - TEXT_HIGHLIGHT_PADDING}
      y={a.position.y - a.fontSize - TEXT_HIGHLIGHT_PADDING / 2}
      width={textWidthApprox + TEXT_HIGHLIGHT_PADDING * 2}
      height={a.fontSize + TEXT_HIGHLIGHT_PADDING * 2}
      fill={highlight.color}
      opacity={highlight.opacity}
      rx={3}
    />
  ) : null;

  // The text anchor `position` is already mapped through the rotated viewport; the glyphs and
  // highlight need to spin around that anchor by the same angle so the text reads upright in the
  // image's rotated frame instead of staying horizontal in the screen frame.
  const groupTransform = rotation ? `rotate(${rotation} ${a.position.x} ${a.position.y})` : undefined;

  if (isEditing) {
    return <G transform={groupTransform}>{highlightRect}</G>;
  }

  return (
    <G transform={groupTransform}>
      {highlightRect}
      <SvgText
        x={a.position.x}
        y={a.position.y}
        fontSize={a.fontSize}
        fill={a.style.strokeColor}
        opacity={a.style.opacity}
        fontFamily={a.fontFamily || 'sans-serif'}
        fontWeight="600"
      >
        {a.text}
      </SvgText>
    </G>
  );
};

const AnnotationShape: FC<AnnotationShapeProps> = ({
  annotation,
  isSelected,
  isEraserHover,
  isHovered,
  isEditing,
  editingText,
  documentRotation = 0,
}) => {
  const highlight = resolveHighlight({ isSelected, isEraserHover, isHovered });

  switch (annotation.type) {
    case AnnotationType.Pen:
    case AnnotationType.Highlighter:
      return renderPenOrHighlighter(annotation as PenAnnotation, highlight);
    case AnnotationType.Arrow:
      return renderArrow(annotation as ArrowAnnotation, highlight);
    case AnnotationType.Rectangle:
      return renderRectangle(annotation as RectangleAnnotation, highlight);
    case AnnotationType.Circle:
      return renderCircle(annotation as CircleAnnotation, highlight);
    case AnnotationType.Text:
      return renderText(annotation as TextAnnotation, highlight, isEditing, editingText, documentRotation);
    default:
      return null;
  }
};

// Memoised — `annotation` references are stable for any annotation that wasn't modified in the
// latest action, and the rest of the props are primitives/undefined. Skipping unchanged shapes
// during a pen stroke removes O(n) JSX work per gesture frame and noticeably reduces latency.
export default React.memo(AnnotationShape);
